/**
 * Every `event:` name the hub's SSE stream can send.
 *
 * `EventSource` has no wildcard listener: a frame with `event: brief.created`
 * is delivered only to a listener registered for that exact name, and never to
 * `onmessage`. So the client has to know the set.
 *
 * The authority is `EventKind` in `@dyspatch/core`, but importing it here would
 * be a *value* import of `@dyspatch/core`, whose barrel pulls in `usage.ts` and
 * `ids.ts` and with them `node:fs` and `node:crypto` -- into a browser bundle.
 * So the list is duplicated, and `packages/hub/test/web.test.ts` fails the build
 * if the two ever disagree. Add a kind to core and the test tells you to add it
 * here.
 */
export const EVENT_KINDS = [
  'brief.created',
  'brief.plan_requested',
  'brief.decomposed',
  'brief.decompose_failed',
  'brief.approved',
  'brief.rejected',
  'brief.cancelled',
  'brief.paused',
  'brief.resumed',
  'brief.amended',
  'brief.check_requested',
  'brief.checked',
  'brief.completed',
  'brief.comment',
  'brief.question_answered',
  'brief.assumptions_accepted',
  'brief.attachment',
  'task.note',
  'task.finding',
  'task.issue_filed',
  'task.created',
  'task.queued',
  'task.offered',
  'task.offer_expired',
  'task.claimed',
  'task.started',
  'task.reporting',
  'task.branch_cut',
  'task.pr_opened',
  'task.pr_ready',
  'task.review',
  'task.changes_requested',
  'task.pr_state',
  'task.fix_filed',
  'task.reassigned',
  'task.done',
  'task.failed',
  'task.cancelled',
  'task.revoke_acked',
  'task.paused',
  'task.resumed',
  'task.pause_acked',
  'task.spec_amended',
  'task.retried',
  'task.timed_out',
  'task.reclaimed',
  'task.held',
  'worker.invited',
  'worker.invite_revoked',
  'worker.enroll_failed',
  'worker.registered',
  'worker.retired',
  'worker.online',
  'worker.offline',
  'worker.cooldown',
  'worker.dnd',
  'worker.declined',
  'person.created',
  'person.role_changed',
  'person.removed',
  'person.password_reset',
  'schedule.approved',
  'schedule.run',
  'schedule.skipped',
  'schedule.paused',
  'schedule.resumed',
  'schedule.removed',
  'notify.sent',
  'notify.failed',
  'helix.synced',
  'helix.issue_filed',
  'repo.added',
  'repo.disabled',
  'repo.enabled',
  'repo.prompt_set',
  'fleet.prompt_set',
] as const;

export type EventKindName = (typeof EVENT_KINDS)[number];

/**
 * The raw output behind a failure, when the event carries one.
 *
 * A failed step's event has two halves: `message`, one paragraph naming what
 * failed and the fix, and `data.detail`, whatever the step actually printed -
 * for a Claude run, the `claude -p` result object with its token counters,
 * timings and session id. The message used to be both, so forty lines of JSON
 * arrived in a 400px pane with the cause buried in them. Everything that reads
 * an event reads the message; only a disclosure reads this.
 *
 * `DyspatchEvent.data` is `unknown` by design - each kind carries its own shape -
 * so this narrows rather than casts, and an event without a detail says so.
 */
export function eventDetail(data: unknown): string | null {
  if (data === null || typeof data !== 'object') return null;
  const detail = (data as { detail?: unknown }).detail;
  return typeof detail === 'string' && detail.trim() !== '' ? detail : null;
}

/**
 * Every event kind's plain-language label, for the reader who has not read
 * `docs/ARCHITECTURE.md` and should not have to.
 *
 * Stripping the family prefix and the underscores (the old `labelForKind`) gave
 * `task.claimed` -> "claimed" and `task.reporting` -> "reporting": still the
 * scheduler's own words, just with the punctuation sanded off. A first-time
 * reader cannot tell `claimed` from `started`, and has no way to guess that
 * `reporting` means the agent is done and pushing a branch. This table is the
 * translation labelForKind (below) was missing: one entry per kind, written for
 * whoever opens the log cold.
 *
 * `subordinate: true` marks the mechanical half of a handshake the log used to
 * print as two events of equal weight: a task is offered to a worker and then
 * claimed by it, one action wearing two events, and an agent reports in before
 * the PR it opened reaches review, again one action in two beats. The primary
 * half (`claimed`, `review`) keeps the reader's attention; the other renders
 * quieter rather than getting folded away outright, so the record stays exactly
 * as long as the journal actually is.
 *
 * The console says "job" wherever a person reads it; the wire word `brief`
 * never appears here.
 */
export interface EventLabel {
  readonly label: string;
  readonly subordinate?: true;
}

export const EVENT_LABELS: Record<EventKindName, EventLabel> = {
  'brief.created': { label: 'Job opened' },
  'brief.plan_requested': { label: 'Plan requested' },
  'brief.decomposed': { label: 'Plan drafted' },
  'brief.decompose_failed': { label: 'Planning failed' },
  'brief.approved': { label: 'Plan approved' },
  'brief.rejected': { label: 'Job rejected' },
  'brief.cancelled': { label: 'Job cancelled' },
  'brief.paused': { label: 'Job paused' },
  'brief.resumed': { label: 'Job resumed' },
  'brief.amended': { label: 'Job changed' },
  'brief.check_requested': { label: 'Integration check asked for' },
  'brief.checked': { label: 'Integration check' },
  'brief.completed': { label: 'Job finished' },
  'brief.comment': { label: 'Comment' },
  'brief.question_answered': { label: 'Question answered' },
  // Go standing over the planner's open forks. Subordinate: the news on that
  // line is the approval itself, and this is the detail of what it decided.
  'brief.assumptions_accepted': { label: 'Assumptions accepted', subordinate: true },
  'brief.attachment': { label: 'File attached' },
  'task.note': { label: 'Note' },
  'task.finding': { label: 'Finding' },
  'task.issue_filed': { label: 'Issue filed' },
  'task.created': { label: 'Task created' },
  'task.queued': { label: 'Queued' },
  // The offer and the claim are the dispatcher's half of one handshake ("this
  // worker, this task") and the worker's half of the same handshake; the claim
  // is the outcome a reader wants, so the offer that led to it renders quieter.
  'task.offered': { label: 'Offered to a worker', subordinate: true },
  'task.offer_expired': { label: 'Offer expired' },
  'task.claimed': { label: 'Claimed' },
  'task.started': { label: 'Started' },
  // Symmetric fold, the other way round: the agent finishing its run is the
  // mechanical half, and the PR reaching a human is the fact worth a reader's
  // full attention, so reporting is what renders quieter here.
  'task.reporting': { label: 'Wrapping up', subordinate: true },
  'task.branch_cut': { label: 'Branch cut' },
  'task.pr_opened': { label: 'PR opened' },
  'task.pr_ready': { label: 'PR marked ready' },
  'task.review': { label: 'Ready for review' },
  'task.changes_requested': { label: 'Sent back' },
  /** Only recorded when the gate's answer moved, so it is never the noise a poll would be. */
  'task.pr_state': { label: 'PR state changed' },
  'task.fix_filed': { label: 'Fix filed' },
  'task.reassigned': { label: 'Reassigned' },
  'task.done': { label: 'Merged' },
  'task.failed': { label: 'Failed' },
  'task.cancelled': { label: 'Cancelled' },
  'task.revoke_acked': { label: 'Stop confirmed' },
  'task.paused': { label: 'Paused' },
  'task.resumed': { label: 'Resumed' },
  'task.pause_acked': { label: 'Pause confirmed' },
  'task.spec_amended': { label: 'Spec changed' },
  'task.retried': { label: 'Retried' },
  'task.timed_out': { label: 'Timed out' },
  'task.reclaimed': { label: 'Reclaimed' },
  'task.held': { label: 'On hold' },
  'worker.invited': { label: 'Agent invited' },
  'worker.invite_revoked': { label: 'Invite revoked' },
  'worker.enroll_failed': { label: 'Enrollment failed' },
  'worker.registered': { label: 'Agent enrolled' },
  'worker.retired': { label: 'Agent retired' },
  'worker.online': { label: 'Agent online' },
  'worker.offline': { label: 'Agent offline' },
  'worker.cooldown': { label: 'Agent cooling down' },
  'worker.dnd': { label: 'Do not disturb' },
  'worker.declined': { label: 'Agent declined' },
  'person.created': { label: 'Member added' },
  'person.role_changed': { label: 'Role changed' },
  'person.removed': { label: 'Member removed' },
  'person.password_reset': { label: 'Temporary password set' },
  'schedule.approved': { label: 'Schedule approved' },
  'schedule.run': { label: 'Schedule ran' },
  'schedule.skipped': { label: 'Schedule skipped' },
  'schedule.paused': { label: 'Schedule paused' },
  'schedule.resumed': { label: 'Schedule resumed' },
  'schedule.removed': { label: 'Schedule removed' },
  'notify.sent': { label: 'Notification sent' },
  'notify.failed': { label: 'Notification failed' },
  'helix.synced': { label: 'Synced from Helix' },
  'helix.issue_filed': { label: 'Filed from Helix' },
  'repo.added': { label: 'Repo registered' },
  'repo.disabled': { label: 'Repo disabled' },
  'repo.enabled': { label: 'Repo re-enabled' },
  'repo.prompt_set': { label: 'Project instructions edited' },
  'fleet.prompt_set': { label: 'Fleet instructions edited' },
};

/**
 * The hub's vocabulary, in the words a person reads it in.
 *
 * An event kind (`task.claimed`) goes through `EVENT_LABELS`. A brief or task
 * status (`claimed`, with no family prefix -- the same word, but never the key
 * of that table, since none of its keys are dot-free) falls through to the old
 * rule: strip a family prefix if there is one, swap underscores for spaces. That
 * rule is still right for a status chip, which has no handshake to fold and no
 * reader arriving without context; it is only the journal that needed a table.
 */
export function labelForKind(kind: string): string {
  const known = EVENT_LABELS[kind as EventKindName];
  if (known) return known.label;
  return kind.replace(/^[a-z]+\./, '').replace(/_/g, ' ');
}

/** True for the mechanical half of a folded pair (`EVENT_LABELS`) -- render it
    quieter than an ordinary line, never hide it: the audit trail stays complete. */
export function isSubordinateKind(kind: string): boolean {
  return EVENT_LABELS[kind as EventKindName]?.subordinate === true;
}

/** The rail's dot colour, by family: plum for a brief, cyan for a machine,
    then the outcome tones, then the gate for anything that needs a person. */
export function eventColor(kind: string): string {
  if (kind.startsWith('brief.')) return 'var(--dy-plan)';
  // People are not machines: the same quiet hue the sidebar gives the People row.
  if (kind.startsWith('person.')) return 'var(--dy-fg-3)';
  if (kind.startsWith('worker.') || kind === 'task.started' || kind === 'task.reporting') {
    return 'var(--dy-info)';
  }
  if (kind.endsWith('.failed') || kind.endsWith('.timed_out') || kind.endsWith('.cancelled')) {
    return 'var(--dy-bad)';
  }
  if (
    kind === 'task.done' ||
    kind === 'task.review' ||
    kind === 'task.pr_opened' ||
    kind === 'task.pr_ready'
  ) {
    return 'var(--dy-ok)';
  }
  if (
    kind === 'task.held' ||
    kind === 'task.changes_requested' ||
    kind === 'task.offer_expired' ||
    kind.endsWith('.paused') ||
    kind === 'task.pause_acked'
  ) {
    return 'var(--dy-warn)';
  }
  return 'var(--dy-accent)';
}
